import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  apiGetCalendarEvents,
  apiCreateCalendarEvent,
  apiUpdateCalendarEvent,
  apiDeleteCalendarEvent,
  apiGetPublicHolidays,
  type CalendarEvent as ApiCalendarEvent,
} from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    addDays,
    addMonths,
    subMonths,
    isSameMonth,
    isSameDay,
    isToday,
    parseISO,
} from "date-fns";
import {
    ChevronLeft,
    ChevronRight,
    Plus,
    CalendarDays,
    Users,
    Megaphone,
    PartyPopper,
    Briefcase,
    X,
    Edit2,
    Flag,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/DatePicker";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { TableSkeleton, ListSkeleton } from "@/components/PageSkeleton";

// ── Types ─────────────────────────────────────────────────────────────────────

type EventCategory = "meeting" | "announcement" | "celebration" | "company" | "other" | "bank_holiday";

interface CorporateEvent {
    id: string;
    title: string;
    date: Date;
    endDate?: Date;
    category: EventCategory;
    description?: string;
    allDay?: boolean;
    time?: string;
    location?: string;
    createdBy?: string;
    country?: string;
    organizer?: string;
    link?: string;
    coverImageUrl?: string;
}

// ── Category config ────────────────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<
    EventCategory,
    { label: string; color: string; dotColor: string; icon: React.ElementType }
> = {
    meeting: {
        label: "Team Meeting",
        color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        dotColor: "bg-blue-500",
        icon: Users,
    },
    announcement: {
        label: "Announcement",
        color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        dotColor: "bg-amber-500",
        icon: Megaphone,
    },
    celebration: {
        label: "Celebration",
        color: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
        dotColor: "bg-pink-500",
        icon: PartyPopper,
    },
    company: {
        label: "Company Event",
        color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
        dotColor: "bg-purple-500",
        icon: Briefcase,
    },
    other: {
        label: "Other",
        color: "bg-muted text-muted-foreground",
        dotColor: "bg-muted-foreground",
        icon: CalendarDays,
    },
    bank_holiday: {
        label: "Bank Holiday",
        color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
        dotColor: "bg-red-500",
        icon: Flag,
    },
};

const CALENDAR_COUNTRY = "North Macedonia";

function nextMondayFromDate(dateStr: string): Date {
    const d = parseISO(dateStr);
    return d.getDay() === 6 ? addDays(d, 2) : addDays(d, 1);
}

// ── Main Component ─────────────────────────────────────────────────────────────

const CorporateCalendar = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [addDialogOpen, setAddDialogOpen] = useState(false);

    useEffect(() => {
      if (searchParams.get("action") === "create") {
        setAddDialogOpen(true);
      }
    }, [searchParams]);

    const [editingEvent, setEditingEvent] = useState<CorporateEvent | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<CorporateEvent | null>(null);
    const [selectedCategories, setSelectedCategories] = useState<EventCategory[]>(
        Object.keys(CATEGORY_CONFIG) as EventCategory[]
    );

    const toggleCategory = (category: EventCategory) => {
        if (selectedCategories.includes(category)) {
            setSelectedCategories(selectedCategories.filter((c) => c !== category));
        } else {
            setSelectedCategories([...selectedCategories, category]);
        }
    };

    const canManageEvents =
        user?.role.mainRole === "admin" || user?.role.mainRole === "hr";

    // ── DB-backed corporate events ─────────────────────────────────────────────
    const { data: dbEvents = [], isLoading: isLoadingEvents } = useQuery<ApiCalendarEvent[]>({
        queryKey: ["corporate-events"],
        queryFn: apiGetCalendarEvents,
        staleTime: 60 * 1000,
    });

    // ── Public holidays from DB (current + next year) ──────────────────────────
    const currentYear = currentMonth.getFullYear();
    const { data: holidaysCurrent = [] } = useQuery({
        queryKey: ["publicHolidays", CALENDAR_COUNTRY, currentYear],
        queryFn: () => apiGetPublicHolidays(CALENDAR_COUNTRY, currentYear),
        staleTime: 5 * 60 * 1000,
    });
    const { data: holidaysNext = [] } = useQuery({
        queryKey: ["publicHolidays", CALENDAR_COUNTRY, currentYear + 1],
        queryFn: () => apiGetPublicHolidays(CALENDAR_COUNTRY, currentYear + 1),
        staleTime: 5 * 60 * 1000,
    });

    const allHolidayRows = [...holidaysCurrent, ...holidaysNext];

    const createMutation = useMutation({
        mutationFn: apiCreateCalendarEvent,
        onSuccess: () => qc.invalidateQueries({ queryKey: ["corporate-events"] }),
    });
    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Omit<ApiCalendarEvent, "id"> }) =>
            apiUpdateCalendarEvent(id, data),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["corporate-events"] }),
    });
    const deleteMutation = useMutation({
        mutationFn: apiDeleteCalendarEvent,
        onSuccess: () => qc.invalidateQueries({ queryKey: ["corporate-events"] }),
    });

    // Convert DB rows to CorporateEvent shape
    const events: CorporateEvent[] = dbEvents.map((e) => ({
        id: e.id,
        title: e.title,
        date: parseISO(e.date.substring(0, 10)),
        endDate: e.endDate ? parseISO(e.endDate.substring(0, 10)) : undefined,
        category: e.category as EventCategory,
        description: e.description ?? undefined,
        allDay: e.allDay,
        time: e.time ?? undefined,
        location: e.location ?? undefined,
        createdBy: e.createdBy ?? undefined,
        organizer: e.organizer ?? undefined,
        link: e.link ?? undefined,
        coverImageUrl: e.coverImageUrl ?? undefined,
    }));

    // Build bank holiday events from DB rows (includes Monday off observed days)
    const bankHolidayEvents: CorporateEvent[] = [];
    for (let i = 0; i < allHolidayRows.length; i++) {
        const h = allHolidayRows[i];
        bankHolidayEvents.push({
            id: `bh-${h.id}`,
            title: h.name,
            date: parseISO(h.date),
            category: "bank_holiday",
            allDay: true,
            country: CALENDAR_COUNTRY,
            createdBy: "System",
        });
        const dow = parseISO(h.date).getDay();
        if (h.next_monday_off && (dow === 0 || dow === 6)) {
            bankHolidayEvents.push({
                id: `bh-${h.id}-obs`,
                title: `${h.name} (Observed)`,
                date: nextMondayFromDate(h.date),
                category: "bank_holiday",
                allDay: true,
                country: CALENDAR_COUNTRY,
                createdBy: "System",
            });
        }
    }

    const allEvents: CorporateEvent[] = [...events, ...bankHolidayEvents];

    // ── New event form state ───────────────────────────────────────────────────
    const [newEvent, setNewEvent] = useState({
        title: "",
        date: format(new Date(), "yyyy-MM-dd"),
        time: "",
        category: "meeting" as EventCategory,
        description: "",
        location: "",
        allDay: false,
    });

    // ── Calendar grid construction ─────────────────────────────────────────────
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days: Date[] = [];
    let day = gridStart;
    while (day <= gridEnd) {
        days.push(day);
        day = addDays(day, 1);
    }

    // ── Event helpers ──────────────────────────────────────────────────────────
    function eventsOnDay(d: Date): CorporateEvent[] {
        return allEvents.filter((e) => isSameDay(e.date, d) && selectedCategories.includes(e.category));
    }

    function upcomingEvents(): CorporateEvent[] {
        const now = new Date();
        return allEvents
            .filter((e) => e.date >= now && selectedCategories.includes(e.category))
            .sort((a, b) => a.date.getTime() - b.date.getTime())
            .slice(0, 6);
    }

    function openAddDialog() {
        setEditingEvent(null);
        setNewEvent({
            title: "",
            date: format(new Date(), "yyyy-MM-dd"),
            time: "",
            category: "meeting",
            description: "",
            location: "",
            allDay: false,
        });
        setAddDialogOpen(true);
    }

    function openEditDialog(ev: CorporateEvent) {
        setEditingEvent(ev);
        setNewEvent({
            title: ev.title,
            date: format(ev.date, "yyyy-MM-dd"),
            time: ev.time ?? "",
            category: ev.category === "bank_holiday" ? "meeting" : ev.category,
            description: ev.description ?? "",
            location: ev.location ?? "",
            allDay: ev.allDay ?? false,
        });
        setSelectedEvent(null);
        setAddDialogOpen(true);
    }

    function handleSaveEvent() {
        if (!newEvent.title.trim()) return;

        const payload = {
            title: newEvent.title,
            date: newEvent.date,
            endDate: null,
            category: newEvent.category,
            description: newEvent.description || null,
            allDay: newEvent.allDay,
            time: newEvent.allDay ? null : (newEvent.time || null),
            location: newEvent.location || null,
            createdBy: user?.name ?? null,
            organizer: editingEvent?.organizer ?? null,
            link: editingEvent?.link ?? null,
            coverImageUrl: editingEvent?.coverImageUrl ?? null,
        };

        if (editingEvent) {
            updateMutation.mutate({ id: editingEvent.id, data: payload });
        } else {
            createMutation.mutate(payload);
        }

        setAddDialogOpen(false);
        setEditingEvent(null);
        setNewEvent({
            title: "",
            date: format(new Date(), "yyyy-MM-dd"),
            time: "",
            category: "meeting",
            description: "",
            location: "",
            allDay: false,
        });
    }

    function handleDeleteEvent(id: string) {
        deleteMutation.mutate(id);
        setSelectedEvent(null);
    }

    function handleEventClick(ev: CorporateEvent) {
        if (ev.category === "bank_holiday") {
            setSelectedEvent(ev);
        } else {
            navigate(`/corporate-calendar/${ev.id}`);
        }
    }

    // ── Render ────────────────────────────────────────────────────────────────
    const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    return (
        <AppLayout>
            <div className="space-y-6 w-full">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                            <CalendarDays className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-foreground">{t("corporateCalendar.title", "Corporate Calendar")}</h1>
                            <p className="text-sm text-muted-foreground mt-1">
                                {t("corporateCalendar.subtitle", "Company events, meetings and announcements")}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Country badge (read-only) */}
                        <div className="flex items-center gap-1.5 h-9 px-3 rounded-md border border-border bg-muted/30 text-sm text-muted-foreground">
                            <Flag className="h-3.5 w-3.5 text-red-500 shrink-0" />
                            {CALENDAR_COUNTRY}
                        </div>

                        {canManageEvents && (
                            <Button
                                onClick={openAddDialog}
                                className="gradient-brand text-primary-foreground h-9"
                            >
                                <Plus className="h-4 w-4 mr-1.5" />
                                {t("corporateCalendar.addEvent", "Add Event")}
                            </Button>
                        )}
                    </div>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-3">
                    {(Object.entries(CATEGORY_CONFIG) as [EventCategory, typeof CATEGORY_CONFIG[EventCategory]][]).map(
                        ([key, cfg]) => {
                            const isSelected = selectedCategories.includes(key);
                            return (
                                <button
                                    key={key}
                                    onClick={() => toggleCategory(key)}
                                    className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${isSelected ? "bg-muted" : "hover:bg-muted/50 opacity-50"
                                        }`}
                                >
                                    <div className={`h-2.5 w-2.5 rounded-full ${cfg.dotColor}`} />
                                    <span className="text-xs text-muted-foreground">{t(`corporateCalendar.categories.${key}`, cfg.label)}</span>
                                </button>
                            );
                        }
                    )}
                </div>

                {isLoadingEvents ? (
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">
                        <Card className="shadow-card">
                            <CardContent className="p-5">
                                <TableSkeleton rows={5} cols={7} />
                            </CardContent>
                        </Card>
                        <ListSkeleton rows={4} />
                    </div>
                ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">
                    {/* Calendar Grid */}
                    <Card className="shadow-card">
                        <CardHeader className="pb-2 pt-4 px-5">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-lg font-semibold text-foreground">
                                    {format(currentMonth, "MMMM yyyy")}
                                </CardTitle>
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 text-xs px-2"
                                        onClick={() => setCurrentMonth(new Date())}
                                    >
                                        {t("common.date.today", "Today")}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="px-3 pb-4">
                            {/* Weekday headers */}
                            <div className="grid grid-cols-7 mb-1">
                                {weekDays.map((wd) => (
                                    <div
                                        key={wd}
                                        className="text-center text-xs font-semibold text-muted-foreground py-2"
                                    >
                                        {wd}
                                    </div>
                                ))}
                            </div>

                            {/* Day cells */}
                            <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
                                {days.map((d, idx) => {
                                    const dayEvents = eventsOnDay(d);
                                    const isCurrentMonth = isSameMonth(d, currentMonth);
                                    const isTodayDate = isToday(d);
                                    const isSelected = selectedDate ? isSameDay(d, selectedDate) : false;
                                    const hasBankHoliday = dayEvents.some((e) => e.category === "bank_holiday");

                                    return (
                                        <div
                                            key={idx}
                                            onClick={() => setSelectedDate(isSameDay(selectedDate ?? new Date(0), d) ? null : d)}
                                            className={`
                        min-h-[80px] p-1.5 cursor-pointer transition-colors
                        ${hasBankHoliday ? "bg-red-50/60 dark:bg-red-950/20" : "bg-card"}
                        ${!isCurrentMonth ? "opacity-40" : ""}
                        ${isSelected ? "bg-primary/5 ring-1 ring-inset ring-primary/30" : "hover:bg-muted/50"}
                      `}
                                        >
                                            <div
                                                className={`
                          h-6 w-6 rounded-full flex items-center justify-center text-xs font-medium mb-1 mx-auto
                          ${isTodayDate
                                                        ? "gradient-brand text-primary-foreground"
                                                        : hasBankHoliday
                                                            ? "text-red-600 dark:text-red-400 font-semibold"
                                                            : "text-foreground"
                                                    }
                        `}
                                            >
                                                {format(d, "d")}
                                            </div>

                                            <div className="space-y-0.5">
                                                {dayEvents.slice(0, 2).map((ev) => {
                                                    const cfg = CATEGORY_CONFIG[ev.category];
                                                    return (
                                                        <button
                                                            key={ev.id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEventClick(ev);
                                                            }}
                                                            className={`
                                w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded font-medium truncate
                                ${cfg.color}
                              `}
                                                        >
                                                            {ev.title}
                                                        </button>
                                                    );
                                                })}
                                                {dayEvents.length > 2 && (
                                                    <p className="text-[10px] text-muted-foreground px-1">
                                                        +{dayEvents.length - 2} {t("corporateCalendar.more", "more")}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Upcoming Events Sidebar */}
                    <div className="space-y-4">
                        {selectedDate && eventsOnDay(selectedDate).length > 0 && (
                            <Card className="shadow-card border-primary/20">
                                <CardHeader className="pb-2 pt-4 px-4">
                                    <CardTitle className="text-sm font-semibold text-foreground">
                                        {format(selectedDate, "EEEE, d MMMM")}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="px-4 pb-4 space-y-2">
                                    {eventsOnDay(selectedDate).map((ev) => {
                                        const cfg = CATEGORY_CONFIG[ev.category];
                                        const Icon = cfg.icon;
                                        return (
                                            <button
                                                key={ev.id}
                                                onClick={() => handleEventClick(ev)}
                                                className="w-full text-left p-2.5 rounded-lg hover:bg-muted transition-colors border border-border"
                                            >
                                                <div className="flex items-start gap-2">
                                                    <div className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${cfg.dotColor} bg-opacity-10`}>
                                                        <Icon className="h-3.5 w-3.5 text-white" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-foreground truncate">{ev.title}</p>
                                                        {ev.time && (
                                                            <p className="text-xs text-muted-foreground">{ev.time}</p>
                                                        )}
                                                        {ev.allDay && (
                                                            <p className="text-xs text-muted-foreground">{t("corporateCalendar.details.allDay", "All day")}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </CardContent>
                            </Card>
                        )}

                        <Card className="shadow-card">
                            <CardHeader className="pb-2 pt-4 px-4">
                                <CardTitle className="text-sm font-semibold text-foreground">
                                    {t("corporateCalendar.upcomingEvents", "Upcoming Events")}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="px-4 pb-4 space-y-2">
                                {upcomingEvents().length === 0 ? (
                                    <p className="text-sm text-muted-foreground py-2">{t("corporateCalendar.noUpcomingEvents", "No upcoming events.")}</p>
                                ) : (
                                    upcomingEvents().map((ev) => {
                                        const cfg = CATEGORY_CONFIG[ev.category];
                                        return (
                                            <button
                                                key={ev.id}
                                                onClick={() => handleEventClick(ev)}
                                                className="w-full text-left flex items-start gap-3 py-2 hover:bg-muted/50 rounded-lg px-1 transition-colors"
                                            >
                                                <div className={`h-2 w-2 rounded-full shrink-0 mt-1.5 ${cfg.dotColor}`} />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-foreground truncate">{ev.title}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {format(ev.date, "EEE, d MMM")}
                                                        {ev.time && ` · ${ev.time}`}
                                                    </p>
                                                </div>
                                            </button>
                                        );
                                    })
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
                )}
            </div>

            {/* ── Event Detail Dialog ──────────────────────────────────────────────── */}
            {selectedEvent && (
                <Dialog open={!!selectedEvent} onOpenChange={(v) => !v && setSelectedEvent(null)}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 pr-8">
                                <div className={`h-3 w-3 rounded-full shrink-0 ${CATEGORY_CONFIG[selectedEvent.category].dotColor}`} />
                                {selectedEvent.title}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3 text-sm">
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <CalendarDays className="h-4 w-4 shrink-0" />
                                <span>
                                    {format(selectedEvent.date, "EEEE, d MMMM yyyy")}
                                    {selectedEvent.allDay
                                        ? ` · ${t("corporateCalendar.details.allDay", "All day")}`
                                        : selectedEvent.time
                                            ? ` · ${selectedEvent.time}`
                                            : ""}
                                </span>
                            </div>
                            {selectedEvent.location && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <span className="text-xs">📍</span>
                                    <span>{selectedEvent.location}</span>
                                </div>
                            )}
                            {selectedEvent.country && selectedEvent.category === "bank_holiday" && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Flag className="h-4 w-4 shrink-0" />
                                    <span>{selectedEvent.country}</span>
                                </div>
                            )}
                            <Badge className={CATEGORY_CONFIG[selectedEvent.category].color}>
                                {t(`corporateCalendar.categories.${selectedEvent.category}`, CATEGORY_CONFIG[selectedEvent.category].label)}
                            </Badge>
                            {selectedEvent.description && (
                                <p className="text-foreground leading-relaxed">{selectedEvent.description}</p>
                            )}
                            {selectedEvent.createdBy && (
                                <p className="text-xs text-muted-foreground">{t("corporateCalendar.details.addedBy", "Added by")} {selectedEvent.createdBy}</p>
                            )}
                        </div>
                        {canManageEvents && selectedEvent.category !== "bank_holiday" && (
                            <DialogFooter className="gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openEditDialog(selectedEvent)}
                                >
                                    <Edit2 className="h-3.5 w-3.5 mr-1" />
                                    {t("corporateCalendar.actions.edit", "Edit Event")}
                                </Button>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleDeleteEvent(selectedEvent.id)}
                                >
                                    <X className="h-3.5 w-3.5 mr-1" />
                                    {t("corporateCalendar.actions.delete", "Delete Event")}
                                </Button>
                            </DialogFooter>
                        )}
                    </DialogContent>
                </Dialog>
            )}

            {/* ── Add / Edit Event Dialog ──────────────────────────────────────────── */}
            {canManageEvents && (
                <Dialog open={addDialogOpen} onOpenChange={(v) => { if (!v) { setAddDialogOpen(false); setEditingEvent(null); } }}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle>{editingEvent ? t("corporateCalendar.form.editTitle", "Edit Event") : t("corporateCalendar.addEvent", "Add Corporate Event")}</DialogTitle>
                        </DialogHeader>
                        <div>
                            <FloatField
                                label={t("corporateCalendar.form.titleLabel", "Event Title *")}
                                required
                                value={newEvent.title}
                                onChange={(v) => setNewEvent((p) => ({ ...p, title: v }))}
                                placeholder={t("corporateCalendar.form.titlePlaceholder", "e.g. All-Hands Meeting")}
                            />

                            <div className="flex gap-3">
                                <FloatWrapper label={t("corporateCalendar.details.date", "Date")} required className="flex-1">
                                    <DatePicker
                                        value={newEvent.date}
                                        onChange={(v) => setNewEvent((p) => ({ ...p, date: v }))}
                                        className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm"
                                    />
                                </FloatWrapper>
                                <FloatField
                                    label={t("corporateCalendar.details.time", "Time")}
                                    type="date"
                                    value={newEvent.time}
                                    onChange={(v) => setNewEvent((p) => ({ ...p, time: v }))}
                                    inputClassName={newEvent.allDay ? "opacity-40 pointer-events-none" : ""}
                                />
                            </div>

                            <div className="flex items-center gap-2 mt-3 px-1">
                                <input
                                    type="checkbox"
                                    id="allDay"
                                    checked={newEvent.allDay}
                                    onChange={(e) => setNewEvent((p) => ({ ...p, allDay: e.target.checked }))}
                                    className="h-4 w-4"
                                />
                                <Label htmlFor="allDay" className="cursor-pointer font-normal text-sm">{t("corporateCalendar.form.allDayCheck", "All day event")}</Label>
                            </div>

                            <FloatWrapper label={t("corporateCalendar.form.category", "Category")}>
                                <Select
                                    value={newEvent.category}
                                    onValueChange={(v) => setNewEvent((p) => ({ ...p, category: v as EventCategory }))}
                                >
                                    <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(Object.entries(CATEGORY_CONFIG) as [EventCategory, typeof CATEGORY_CONFIG[EventCategory]][])
                                            .filter(([key]) => key !== "bank_holiday")
                                            .map(([key, cfg]) => (
                                                <SelectItem key={key} value={key}>{t(`corporateCalendar.categories.${key}`, cfg.label)}</SelectItem>
                                            ))}
                                    </SelectContent>
                                </Select>
                            </FloatWrapper>

                            <FloatField
                                label={t("corporateCalendar.form.locationLabel", "Location")}
                                value={newEvent.location}
                                onChange={(v) => setNewEvent((p) => ({ ...p, location: v }))}
                                placeholder={t("corporateCalendar.form.locationPlaceholder", "e.g. Conference Room A / Zoom")}
                            />

                            <FloatField
                                label={t("corporateCalendar.form.descLabel", "Description")}
                                type="textarea"
                                value={newEvent.description}
                                onChange={(v) => setNewEvent((p) => ({ ...p, description: v }))}
                                placeholder={t("corporateCalendar.form.descPlaceholder", "Event details...")}
                            />
                        </div>
                        <DialogFooter className="mt-2">
                            <Button variant="outline" onClick={() => { setAddDialogOpen(false); setEditingEvent(null); }}>
                                {t("corporateCalendar.actions.cancel", "Cancel")}
                            </Button>
                            <Button
                                onClick={handleSaveEvent}
                                disabled={!newEvent.title.trim()}
                                className="gradient-brand text-primary-foreground"
                            >
                                {editingEvent ? t("corporateCalendar.actions.save", "Save Changes") : t("corporateCalendar.addEvent", "Add Event")}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </AppLayout>
    );
};

export default CorporateCalendar;
